import React, { useRef, useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import toastr from 'toastr';

const TYPING_IDLE_MS = 3000;
const REC_HOLD_THRESHOLD_MS = 250;   // удержание дольше — реальная запись
const REC_MIN_DURATION_MS = 800;     // меньше — слишком короткое, не отправляем
const REC_MAX_SECONDS = 180;         // 3 минуты

export default function MessageInput({
    disabled, disabledReason,
    replyTo, onCancelReply,
    sendTyping, sendTypingStop,
    knownUsers
}) {
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef(null);
    const taRef = useRef(null);
    const [pollOpen, setPollOpen] = useState(false);

    const [mentionQuery, setMentionQuery] = useState(null);
    const [mentionIdx, setMentionIdx] = useState(0);

    const isTypingRef = useRef(false);
    const idleTimerRef = useRef(null);

    // ── Voice recording (hold-to-record) ──
    const [recState, setRecState] = useState('idle'); // 'idle' | 'arming' | 'recording' | 'uploading'
    const [recSeconds, setRecSeconds] = useState(0);
    const recRef = useRef(null);
    const recChunksRef = useRef([]);
    const recStreamRef = useRef(null);
    const recTimerRef = useRef(null);
    const recCancelRef = useRef(false);
    const recStartedAtRef = useRef(0);
    const recArmTimerRef = useRef(null);

    const stopTyping = useCallback(() => {
        if (isTypingRef.current) {
            isTypingRef.current = false;
            if (sendTypingStop) sendTypingStop();
        }
        if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    }, [sendTypingStop]);

    const armIdleTimer = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(stopTyping, TYPING_IDLE_MS);
    }, [stopTyping]);

    useEffect(() => () => stopTyping(), [stopTyping]);

    const onChange = (e) => {
        const v = e.target.value;
        setText(v);
        const caretPos = e.target.selectionStart;
        const before = v.slice(0, caretPos);
        const m = /(^|\s)@([A-Za-z0-9_.\-]{0,40})$/.exec(before);
        if (m) {
            setMentionQuery(m[2].toLowerCase());
            setMentionIdx(0);
        } else {
            setMentionQuery(null);
        }

        if (v.trim().length === 0) {
            stopTyping();
        } else {
            isTypingRef.current = true;
            if (sendTyping) sendTyping();
            armIdleTimer();
        }

        const ta = e.target;
        ta.style.height = 'auto';
        ta.style.height = Math.min(140, ta.scrollHeight) + 'px';
    };

    const filteredMentions = mentionQuery == null ? [] :
        (knownUsers || []).filter(u => u.login.toLowerCase().includes(mentionQuery)).slice(0, 8);

    const insertMention = (login) => {
        const ta = taRef.current;
        const pos = ta ? ta.selectionStart : text.length;
        const before = text.slice(0, pos).replace(/@([A-Za-z0-9_.\-]{0,40})$/, '@' + login + ' ');
        const after = text.slice(pos);
        const next = before + after;
        setText(next);
        setMentionQuery(null);
        setTimeout(() => {
            if (ta) {
                ta.focus();
                const np = before.length;
                ta.setSelectionRange(np, np);
            }
        }, 0);
    };

    const sendText = async () => {
        const t = text.trim();
        if (!t || sending) return;
        setSending(true);
        stopTyping();
        try {
            await api.chat.sendText(t, replyTo?._id || null);
            setText('');
            if (taRef.current) taRef.current.style.height = 'auto';
            if (onCancelReply) onCancelReply();
        } catch (e) {
            toastr.warning(' ', e.response?.data?.message || 'Ошибка отправки.');
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (filteredMentions.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape')) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, filteredMentions.length - 1)); return; }
            if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filteredMentions[mentionIdx].login); return; }
            if (e.key === 'Escape')    { setMentionQuery(null); return; }
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendText();
        }
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        e.target.value = '';
        setSending(true);
        try {
            await api.chat.upload(f, '', replyTo?._id || null);
            if (onCancelReply) onCancelReply();
        } catch (err) {
            toastr.warning(' ', err.response?.data?.message || 'Ошибка загрузки.');
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        const handler = async (e) => {
            const items = e.clipboardData?.items || [];
            for (const it of items) {
                if (it.type && it.type.startsWith('image/')) {
                    const f = it.getAsFile();
                    if (f) {
                        e.preventDefault();
                        setSending(true);
                        try { await api.chat.upload(f, '', replyTo?._id || null); if (onCancelReply) onCancelReply(); }
                        catch (err) { toastr.warning(' ', err.response?.data?.message || 'Ошибка загрузки.'); }
                        finally { setSending(false); }
                        return;
                    }
                }
            }
        };
        const ta = taRef.current;
        if (ta) ta.addEventListener('paste', handler);
        return () => { if (ta) ta.removeEventListener('paste', handler); };
    }, [replyTo, onCancelReply]);

    // ── Voice recording (как в Telegram) ────────────────────────────────────
    const supportsRecording = typeof window !== 'undefined' &&
        navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== 'undefined';

    const pickAudioMime = () => {
        const candidates = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/mp4'
        ];
        for (const m of candidates) { if (MediaRecorder.isTypeSupported(m)) return m; }
        return '';
    };

    const startRecording = async () => {
        if (!supportsRecording) { toastr.warning(' ', 'Запись звука не поддерживается браузером.'); setRecState('idle'); return; }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Если пользователь уже отпустил кнопку до того, как браузер выдал доступ — не стартуем
            if (recCancelRef.current) {
                stream.getTracks().forEach(t => t.stop());
                setRecState('idle');
                return;
            }
            recStreamRef.current = stream;
            const mime = pickAudioMime();
            const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            recRef.current = mr;
            recChunksRef.current = [];

            mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) recChunksRef.current.push(ev.data); };
            mr.onstop = async () => {
                try { recStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
                recStreamRef.current = null;
                if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }

                const duration = Date.now() - recStartedAtRef.current;

                if (recCancelRef.current || duration < REC_MIN_DURATION_MS) {
                    recChunksRef.current = [];
                    setRecState('idle'); setRecSeconds(0);
                    if (!recCancelRef.current) toastr.info(' ', 'Слишком короткое — отпустите чуть дольше.');
                    return;
                }

                const blobMime = (mime || 'audio/webm').split(';')[0];
                const blob = new Blob(recChunksRef.current, { type: blobMime });
                recChunksRef.current = [];
                if (blob.size < 800) { setRecState('idle'); setRecSeconds(0); return; }

                const ext = blobMime.includes('mp4') ? '.m4a' : blobMime.includes('ogg') ? '.ogg' : '.webm';
                const file = new File([blob], `voice-${Date.now()}${ext}`, { type: blobMime });

                setRecState('uploading');
                try {
                    const seconds = Math.max(1, Math.round(duration / 1000));
                    await api.chat.upload(file, '', replyTo?._id || null, null, seconds);
                    if (onCancelReply) onCancelReply();
                } catch (err) {
                    toastr.warning(' ', err.response?.data?.message || 'Не удалось отправить голосовое.');
                } finally {
                    setRecState('idle'); setRecSeconds(0);
                }
            };

            // timeslice 1000 мс — flush чанков каждую секунду.
            // Это ОБЯЗАТЕЛЬНО для WebM-blobs от MediaRecorder, иначе в финальном
            // файле отсутствует duration metadata и браузер показывает 0:00 / Infinity.
            mr.start(1000);
            recStartedAtRef.current = Date.now();
            setRecState('recording');
            setRecSeconds(0);
            recTimerRef.current = setInterval(() => {
                setRecSeconds(s => {
                    const next = s + 1;
                    if (next >= REC_MAX_SECONDS) { stopRecording(false); }
                    return next;
                });
            }, 1000);
        } catch (e) {
            toastr.warning(' ', 'Нет доступа к микрофону.');
            setRecState('idle');
        }
    };

    const stopRecording = (cancel) => {
        recCancelRef.current = !!cancel;
        try { recRef.current?.stop(); } catch (_) {}
    };

    // ── Hold-to-record на «➤» когда нет текста ─────────────────────────────
    const isVoiceMode = !text.trim() && !sending && supportsRecording && !disabled;

    // recState читаем через ref чтобы window-listener'ы не нужно было пересоздавать
    const recStateRef = useRef('idle');
    useEffect(() => { recStateRef.current = recState; }, [recState]);

    const finishHold = useCallback((cancel) => {
        // Снимаем глобальные слушатели
        window.removeEventListener('mouseup',    onGlobalUp);
        window.removeEventListener('touchend',   onGlobalUp);
        window.removeEventListener('touchcancel', onGlobalCancel);

        if (recArmTimerRef.current) {
            // Короткий тап — micro-press до старта записи
            clearTimeout(recArmTimerRef.current);
            recArmTimerRef.current = null;
            setRecState('idle');
            if (!cancel) toastr.info(' ', 'Зажмите кнопку, чтобы записать голосовое.');
            return;
        }
        if (recStateRef.current === 'recording') {
            stopRecording(cancel);
        } else if (recStateRef.current === 'arming') {
            // Ещё не успели стартовать — просто отменяем
            recCancelRef.current = true;
            setRecState('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onGlobalUp()      { finishHold(false); }
    function onGlobalCancel()  { finishHold(true); }

    const beginPress = (e) => {
        if (!isVoiceMode) return;                  // если есть текст — обычная кнопка отправки
        // touchstart по умолчанию passive в современных браузерах — preventDefault
        // там вызывает warning. Проверяем cancelable.
        if (e.cancelable) try { e.preventDefault(); } catch (_) {}
        recCancelRef.current = false;
        setRecState('arming');

        // Глобальные listener'ы — кнопка может исчезнуть из DOM при смене режима,
        // поэтому ловим отпускание на уровне window. Иначе на ПК после старта
        // записи mouseup не приходит и пользователь застревает.
        window.addEventListener('mouseup',    onGlobalUp);
        window.addEventListener('touchend',   onGlobalUp);
        window.addEventListener('touchcancel', onGlobalCancel);

        recArmTimerRef.current = setTimeout(() => {
            recArmTimerRef.current = null;
            if (recCancelRef.current) { setRecState('idle'); return; }
            startRecording();
        }, REC_HOLD_THRESHOLD_MS);
    };

    useEffect(() => () => {
        if (recArmTimerRef.current) clearTimeout(recArmTimerRef.current);
        window.removeEventListener('mouseup',    onGlobalUp);
        window.removeEventListener('touchend',   onGlobalUp);
        window.removeEventListener('touchcancel', onGlobalCancel);
        try { recRef.current?.stop(); } catch (_) {}
        try { recStreamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
        if (recTimerRef.current) clearInterval(recTimerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="pchat-input">
            {replyTo && (
                <div className="pchat-reply-banner">
                    <span>↩ Ответ <strong>{replyTo.fromLogin}</strong>: {(replyTo.text || '[вложение]').slice(0, 80)}</span>
                    <button onClick={onCancelReply} title="Отменить ответ">✕</button>
                </div>
            )}
            {filteredMentions.length > 0 && (
                <div className="pchat-mention-drop">
                    {filteredMentions.map((u, i) =>
                        <div
                            key={u.login}
                            className={`pchat-mention-item ${i === mentionIdx ? 'active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); insertMention(u.login); }}
                        >@{u.login}{u.role === 'admin' ? ' • админ' : ''}</div>
                    )}
                </div>
            )}

            {recState === 'recording' || recState === 'arming' ? (
                <div className="pchat-rec-bar">
                    <span className="pchat-rec-dot" />
                    <span className="pchat-rec-time">{fmtSec(recSeconds)}</span>
                    <span className="pchat-rec-hint">
                        {recState === 'arming' ? 'Удерживайте…' : 'Отпустите для отправки'}
                    </span>
                    <button
                        type="button"
                        className="pchat-icon-btn"
                        title="Отменить запись"
                        onClick={() => stopRecording(true)}
                    >🗑</button>
                    <button
                        type="button"
                        className="pchat-send-btn"
                        title="Отправить голосовое"
                        onClick={() => stopRecording(false)}
                    >➤</button>
                </div>
            ) : recState === 'uploading' ? (
                <div className="pchat-rec-bar">
                    <span className="pchat-rec-hint">Отправляем голосовое…</span>
                </div>
            ) : (
                <>
                    <button
                        type="button"
                        className="pchat-icon-btn"
                        title="Прикрепить файл"
                        disabled={disabled || sending}
                        onClick={() => fileInputRef.current?.click()}
                    >📎</button>
                    <button
                        type="button"
                        className="pchat-icon-btn"
                        title="Создать опрос"
                        disabled={disabled || sending}
                        onClick={() => setPollOpen(true)}
                    >📊</button>
                    <textarea
                        ref={taRef}
                        className="pchat-textarea"
                        placeholder={disabled ? (disabledReason || 'Отправка отключена') : 'Сообщение… (Enter — отправить, Shift+Enter — новая строка)'}
                        value={text}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onBlur={() => stopTyping()}
                        disabled={disabled || sending}
                        rows={1}
                    />
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFile} />
                    <button
                        type="button"
                        className={`pchat-send-btn ${isVoiceMode ? 'voice-mode' : ''}`}
                        onClick={() => { if (!isVoiceMode) sendText(); }}
                        onMouseDown={beginPress}
                        onTouchStart={beginPress}
                        onContextMenu={(e) => e.preventDefault()}
                        disabled={disabled || sending || (!isVoiceMode && !text.trim())}
                        title={isVoiceMode ? 'Зажмите для записи голосового' : 'Отправить'}
                    >{isVoiceMode ? '🎤' : '➤'}</button>
                </>
            )}
            {pollOpen && (
                <PollCreator
                    onClose={() => setPollOpen(false)}
                    replyTo={replyTo?._id || null}
                    onSent={() => { setPollOpen(false); if (onCancelReply) onCancelReply(); }}
                />
            )}
        </div>
    );
}

function PollCreator({ onClose, onSent, replyTo }) {
    const [q, setQ] = useState('');
    const [opts, setOpts] = useState(['', '']);
    const [multi, setMulti] = useState(false);
    const [busy, setBusy] = useState(false);

    const send = async () => {
        const cleanOpts = opts.map(o => o.trim()).filter(Boolean);
        if (!q.trim() || cleanOpts.length < 2) {
            toastr.warning(' ', 'Введите вопрос и минимум 2 варианта.'); return;
        }
        setBusy(true);
        try {
            await api.chat.sendPoll({
                question: q.trim(),
                options: cleanOpts.map(t => ({ text: t })),
                allowMultiple: multi
            }, replyTo);
            onSent();
        } catch (e) {
            toastr.warning(' ', e.response?.data?.message || 'Ошибка.');
        } finally { setBusy(false); }
    };
    return (
        <div className="pchat-modal-back" onClick={onClose}>
            <div className="pchat-modal" onClick={e => e.stopPropagation()}>
                <h3>📊 Новый опрос</h3>
                <input
                    className="pchat-modal-input"
                    placeholder="Вопрос"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                />
                {opts.map((o, i) => (
                    <div className="pchat-modal-row" key={i}>
                        <input
                            className="pchat-modal-input"
                            placeholder={`Вариант ${i + 1}`}
                            value={o}
                            onChange={e => { const c = [...opts]; c[i] = e.target.value; setOpts(c); }}
                        />
                        {opts.length > 2 && (
                            <button className="pchat-btn" onClick={() => setOpts(opts.filter((_, j) => j !== i))}>✕</button>
                        )}
                    </div>
                ))}
                {opts.length < 10 && <button className="pchat-btn" onClick={() => setOpts([...opts, ''])}>+ Добавить вариант</button>}
                <div style={{ marginTop: 12, fontSize: 13 }}>
                    <label style={{ cursor: 'pointer' }}>
                        <input type="checkbox" checked={multi} onChange={e => setMulti(e.target.checked)} style={{ marginRight: 6 }} />
                        Можно выбрать несколько вариантов
                    </label>
                </div>
                <div className="pchat-modal-actions">
                    <button className="pchat-btn" onClick={onClose}>Отмена</button>
                    <button className="pchat-btn primary" disabled={busy} onClick={send}>Опубликовать</button>
                </div>
            </div>
        </div>
    );
}
